/* istanbul ignore file */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown, faEarth, faEllipsisVertical, faTag,
} from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import { RaaRoles } from '../../../../utility/SecurityRules';
import EditableTextField from '../../../common/EditableTextField/EditableTextField';
import EntityType from '../../../../utility/EntityType';
import { ApplyTagsModal } from '../../../guidance/tags/modals/ApplyTagsModal/ApplyTagsModal';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import {
  createDevicePublicLink,
  deleteDevicePublicLink,
} from '../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import envConfig from '../../../../envConfig';
import { DownloadDataModal } from '../modals/DownloadDataModal';
import { EditLocationModal } from '../modals/EditLocationModal';
import DevicePublicLinkModal from '../modals/DevicePublicLinkModal';

const CustomToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    type="button"
    className="btn bg-secondary rounded p-0 d-flex align-items-center"
    style={{ width: '40px', height: '38px' }}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <div className="d-flex align-items-center justify-content-center" style={{ width: '50%' }}>
      <FontAwesomeIcon
        icon={faEllipsisVertical}
        size="lg"
        className="text-white"
      />
    </div>
    <div className="d-flex align-items-center justify-content-center" style={{ width: '50%' }}>
      <FontAwesomeIcon
        icon={faCaretDown}
        size="sm"
        className="text-white"
      />
    </div>
  </button>
));

CustomToggle.propTypes = {
  onClick: PropTypes.func.isRequired,
};

// eslint-disable-next-line react/prop-types
const DescriptionComponent = ({ deviceName }) => <span>{deviceName}</span>;

function DeviceDetailsHeader({
  deviceInfo, handleSubmitNameChange, setModalJSX, isPublic,
}) {
  const dispatch = useDispatch();
  const [publicPageHash, setPublicPageHash] = React.useState(deviceInfo.publicPageHash);
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = React.useState(false);

  // todo: changing this to redirect to php public device page for now
  const publicReportUrl = publicPageHash
    ? `${envConfig.raaUrl}/public/device/${publicPageHash}`
    : '';

  const handleOpenDownloadDataModal = () => {
    // if (isPublic) return;
    setModalJSX((
      <DownloadDataModal setModalJSX={setModalJSX} deviceId={deviceInfo.id} />
    ));
  };

  const handleOpenApplyTagsModal = () => {
    if (isPublic) return;
    const selectedDevices = [deviceInfo.id];
    const tagIds = deviceInfo.tagList.map((tag) => tag.id);
    setModalJSX(
      <ApplyTagsModal
        selectedIdList={selectedDevices}
        entityType={EntityType.DEVICE}
        tagsAssociatedWithAll={tagIds}
        tagsAssociatedWithSome={tagIds}
        setModalJSX={setModalJSX}
      />,
    );
  };

  const handleDeletePublicLink = async () => {
    if (isPublic) return false;
    try {
      setIsLoading(true);
      await dispatch(deleteDevicePublicLink({ deviceId: deviceInfo.id })).unwrap();
      setPublicPageHash(null);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error: ', err);
      // setModalJSX(<GenericErrorModal show setModalJSX={setModalJSX} message={err.message} />);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  const handleCreatePublicLink = async () => {
    if (isPublic) return null;
    try {
      setIsLoading(true);
      const result = await dispatch(createDevicePublicLink({ deviceId: deviceInfo.id })).unwrap();
      const newPublicLink = result.publicLink;
      setPublicPageHash(newPublicLink);
      return `${envConfig.raaUrl}/public/device/${newPublicLink}`;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error: ', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPublicLinkModal = () => {
    if (isPublic) return;
    setModalJSX(
      <DevicePublicLinkModal
        initialPublicLinkUrl={publicReportUrl}
        onClose={() => setModalJSX((<></>))}
        userHasEditPrivileges
        onDeletePublicLink={handleDeletePublicLink}
        onCreatePublicLink={handleCreatePublicLink}
        isLoading={isLoading}
      />,
    );
  };

  const handleOpenEditLocationModal = () => {
    if (isPublic) return;
    setModalJSX(
      <EditLocationModal setModalJSX={setModalJSX} currentLocation={deviceInfo.location} />,
    );
  };

  return (
    <div className="clearfix">
      <div className="float-left">
        <div className="row mb-2">
          <div className="col">
            <h4 className="my-0">
              <HasRolePrivileges
                targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}
                alternativeComponent={<DescriptionComponent deviceName={deviceInfo.name} />}
              >
                <EditableTextField
                  originalTitle={deviceInfo.name}
                  userHasTitleEditPrivileges
                  saveTitle={handleSubmitNameChange}
                  fontSize={2}
                  noPadding
                  allowEmptyTitle
                />
              </HasRolePrivileges>
            </h4>
            <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
              <div className="mt-1 px-1">
                <span className="p-0 mb-0" onClick={handleOpenApplyTagsModal} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faTag} className="mr-1" />
                  {deviceInfo.tagList.length > 0 ? deviceInfo.tagList.map((tag) => tag.name).join(', ') : 'No tags set'}
                </span>
              </div>
              <div className="mb-1 px-1">
                <span onClick={handleOpenEditLocationModal} className="p-0 mb-0" style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faEarth} className="mr-1" />
                  {deviceInfo.location ? deviceInfo.location : 'No location set'}
                </span>
              </div>
            </HasRolePrivileges>

          </div>
        </div>
      </div>
      <div className="float-right">

        <div className="mr-2 pt-2">
          <Dropdown>
            <Dropdown.Toggle as={CustomToggle} id="dropdown-context-menu" />
            <Dropdown.Menu>
              <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
                <Dropdown.Item onClick={handleOpenPublicLinkModal}>
                  Manage Public Link
                </Dropdown.Item>
              </HasRolePrivileges>

              <Dropdown.Item onClick={handleOpenDownloadDataModal}>
                Download Device Data
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>

  );
}

DeviceDetailsHeader.propTypes = {
  deviceInfo: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    location: PropTypes.string,
    uptime: PropTypes.string,
    pushStatus: PropTypes.number,
    isLinkConnected: PropTypes.bool,
    publicPageHash: PropTypes.string,
    linkName: PropTypes.string,
    linkId: PropTypes.number,
    ethIp: PropTypes.string,
    wlanIp: PropTypes.string,
    isHttps: PropTypes.bool,
    httpPort: PropTypes.number,
    macAddress: PropTypes.string,
    serialnumber: PropTypes.string,
    firmware: PropTypes.string,
    type: PropTypes.string,
    tagList: PropTypes.array,
    warrantyEndDate: PropTypes.string,
    pushInterval: PropTypes.number,
    lastMessageTimestamp: PropTypes.shape({ epochSecond: PropTypes.string }),
    assignmentStartDate: PropTypes.shape({ epochSecond: PropTypes.string }),
  }).isRequired,
  handleSubmitNameChange: PropTypes.func.isRequired,
  setModalJSX: PropTypes.func.isRequired,
  isPublic: PropTypes.bool.isRequired,
};

export { DeviceDetailsHeader };
export default connectModalContainerContextToComponent(DeviceDetailsHeader);
